import { ApiReportActionTypes } from "../types/apiReportState.types";
import { ClientSettingsActionTypes } from "../types/clientSettingsState.types";
import { CurrentReportActionTypes } from "../types/currentReportState.types";
import { DevToolsConfigurationActionTypes } from "../types/devToolsState.types";
import { DrillDownConfigurationActionTypes } from "../types/drillDownConfigurationState.types";
import { DrillDownPageActionTypes } from "../types/drillDownPageState.types";
import {
  ChartFieldsActionTypes,
  FieldsStateActionTypes,
  PivotFieldsActionTypes,
  TabularFieldsActionTypes,
  TileFieldsActionTypes,
} from "../types/fieldsState.types";
import { MeasureDatasetsActionTypes } from "../types/measureDatasetsState.types";
import { MetaDataActionTypes } from "../types/metaDataState.types";
import { ReportsActionTypes } from "../types/reportsState.types";
import { ReportTemplatesActionTypes } from "../types/reportTemplatesState.types";
import { UiSettingsActionTypes } from "../types/uiSettingsState.types";

export const TILE_SLICE_NAME = "tileFields";
export const TABULAR_SLICE_NAME = "tabularFields";
export const PIVOT_SLICE_NAME = "pivotFields";
export const CHART_SLICE_NAME = "chartFields";
export const META_DATA_SLICE_NAME = "metaData";
export const REPORT_TEMPLATES_SLICE_NAME = "reportTemplates";
export const REPORTS_SLICE_NAME = "reports";
export const MEASURE_DATASETS_SLICE_NAME = "measureDatasets";
export const FIELDS_STATE_SLICE_NAME = "fieldsState";
export const DRILL_DOWN_PAGE_SLICE_NAME = "drillDownPage";
export const DRILL_DOWN_CONFIGURATION_SLICE_NAME = "drillDownConfiguration";
export const DEV_TOOLS_SLICE_NAME = "devTools";
export const CURRENT_REPORT_SLICE_NAME = "currentReport";
export const CLIENT_SETTINGS_SLICE_NAME = "clientSettings";
export const API_REPORT_SLICE_NAME = "apiReport";
export const UI_SETTINGS_SLICE_NAME = "uiSettings";

type ActionTypes = {
  tabular: TabularFieldsActionTypes;
  pivot: PivotFieldsActionTypes;
  chart: ChartFieldsActionTypes;
  metaData: MetaDataActionTypes;
  reportTemplates: ReportTemplatesActionTypes;
  reports: ReportsActionTypes;
  measureDatasets: MeasureDatasetsActionTypes;
  fieldsState: FieldsStateActionTypes;
  drillDownPage: DrillDownPageActionTypes;
  drillDownConfiguration: DrillDownConfigurationActionTypes;
  devTools: DevToolsConfigurationActionTypes;
  currentReport: CurrentReportActionTypes;
  clientSettings: ClientSettingsActionTypes;
  apiReport: ApiReportActionTypes;
  uiSettings: UiSettingsActionTypes;
  tile: TileFieldsActionTypes;
};

type SliceNames = keyof typeof sliceNames;
const sliceNames = {
  tabular: TABULAR_SLICE_NAME,
  pivot: PIVOT_SLICE_NAME,
  chart: CHART_SLICE_NAME,
  metaData: META_DATA_SLICE_NAME,
  reportTemplates: REPORT_TEMPLATES_SLICE_NAME,
  reports: REPORTS_SLICE_NAME,
  measureDatasets: MEASURE_DATASETS_SLICE_NAME,
  fieldsState: FIELDS_STATE_SLICE_NAME,
  drillDownPage: DRILL_DOWN_PAGE_SLICE_NAME,
  drillDownConfiguration: DRILL_DOWN_CONFIGURATION_SLICE_NAME,
  devTools: DEV_TOOLS_SLICE_NAME,
  currentReport: CURRENT_REPORT_SLICE_NAME,
  clientSettings: CLIENT_SETTINGS_SLICE_NAME,
  apiReport: API_REPORT_SLICE_NAME,
  uiSettings: UI_SETTINGS_SLICE_NAME,
  tile: TILE_SLICE_NAME,
} as const;

const createAction = <S extends SliceNames, T extends keyof ActionTypes[S]>(
  slice: S,
  type: T,
  payload?: ActionTypes[S][T] extends { payload: infer P } ? P : never
) => ({
  type: `${sliceNames[slice]}/${String(type)}`,
  payload,
});

export const createTileAction = <T extends keyof TileFieldsActionTypes>(
  type: T,
  payload?: TileFieldsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("tile", type, payload);

export const createTabularAction = <T extends keyof TabularFieldsActionTypes>(
  type: T,
  payload?: TabularFieldsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("tabular", type, payload);

export const createPivotAction = <T extends keyof PivotFieldsActionTypes>(
  type: T,
  payload?: PivotFieldsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("pivot", type, payload);

export const createChartAction = <T extends keyof ChartFieldsActionTypes>(
  type: T,
  payload?: ChartFieldsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("chart", type, payload);

export const createMetaDataAction = <T extends keyof MetaDataActionTypes>(
  type: T,
  payload?: MetaDataActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("metaData", type, payload);

export const createReportTemplatesAction = <T extends keyof ReportTemplatesActionTypes>(
  type: T,
  payload?: ReportTemplatesActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("reportTemplates", type, payload);

export const createReportsAction = <T extends keyof ReportsActionTypes>(
  type: T,
  payload?: ReportsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("reports", type, payload);

export const createMeasureDatasetsAction = <T extends keyof MeasureDatasetsActionTypes>(
  type: T,
  payload?: MeasureDatasetsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("measureDatasets", type, payload);

export const createFieldsStateAction = <T extends keyof FieldsStateActionTypes>(
  type: T,
  payload?: FieldsStateActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("fieldsState", type, payload);

export const createDrillDownPageAction = <T extends keyof DrillDownPageActionTypes>(
  type: T,
  payload?: DrillDownPageActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("drillDownPage", type, payload);

export const createDrillDownConfigurationAction = <T extends keyof DrillDownConfigurationActionTypes>(
  type: T,
  payload?: DrillDownConfigurationActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("drillDownConfiguration", type, payload);

export const createDevToolsAction = <T extends keyof DevToolsConfigurationActionTypes>(
  type: T,
  payload?: DevToolsConfigurationActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("devTools", type, payload);

export const createCurrentReportAction = <T extends keyof CurrentReportActionTypes>(
  type: T,
  payload?: CurrentReportActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("currentReport", type, payload);

export const createClientSettingsAction = <T extends keyof ClientSettingsActionTypes>(
  type: T,
  payload?: ClientSettingsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("clientSettings", type, payload);

export const createApiReportAction = <T extends keyof ApiReportActionTypes>(
  type: T,
  payload?: ApiReportActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("apiReport", type, payload);

export const createUiSettingsAction = <T extends keyof UiSettingsActionTypes>(
  type: T,
  payload?: UiSettingsActionTypes[T] extends { payload: infer P } ? P : never
) => createAction("uiSettings", type, payload);
